import React from 'react';
import Navbar from '@/components/Navbar';
import { Heart, Leaf, Users, Globe } from 'lucide-react';

export default function WhoWeArePage() {
  return (
    <div 
      className="min-h-screen" 
      style={{ 
        backgroundImage: 'linear-gradient(rgba(129, 216, 208, 0.92), rgba(129, 216, 208, 0.92)), url(https://images.unsplash.com/photo-1596398998950-34508ed6d173?q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 
            className="text-5xl md:text-6xl font-bold text-white mb-6" 
            style={{ fontFamily: 'Playfair Display, serif', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
            data-testid="who-we-are-title"
          >
            Who We Are
          </h1>
          <p className="text-xl text-white" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            A Family Legacy of Natural Healing & Ancestral Wisdom
          </p>
        </div>

        {/* Main Story */}
        <div className="rounded-2xl shadow-2xl p-8 md:p-12 mb-8" style={{ background: '#FFDAB9' }}>
          <div className="flex items-center justify-center mb-8">
            <Heart className="w-12 h-12 text-terracotta-600" />
          </div>
          
          <h2 className="text-3xl font-bold text-sage-900 mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Meet Tonja & John
          </h2>
          
          <div className="prose prose-lg max-w-none text-sage-800 space-y-6">
            <p className="text-lg leading-relaxed">
              JFRAI Family Flavorful Brews & Tings is more than a business—it's a love story woven through generations, 
              cultures, and a deep commitment to natural wellness. Founded by Tonja and John, our company represents 
              the beautiful fusion of rich ancestral traditions and a shared passion for healing through nature's gifts.
            </p>

            <div className="bg-sage-50 p-6 rounded-xl my-8">
              <div className="flex items-start gap-4 mb-6">
                <Globe className="w-8 h-8 text-sage-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-semibold text-sage-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Tonja's Heritage: West Indian & Southern Roots
                  </h3>
                  <p className="text-sage-700 leading-relaxed">
                    Tonja brings the vibrant spirit of Trinidad to everything we create. Growing up surrounded by the 
                    rich herbal traditions of the Caribbean, she learned from her paternal grandmother and dad the sacred 
                    art of bush medicine—natural remedies passed down through generations. From aromatic teas made with 
                    fresh lemongrass to healing oils infused with island botanicals, Trinidadian wisdom runs deep in her veins.
                  </p>
                  <p className="text-sage-700 leading-relaxed mt-4">
                    Her Southern roots from Alabama add another layer of ancestral knowledge. Alabama has long held deep 
                    traditions of herbal healing, from sassafras teas to healing salves made with locally grown herbs. 
                    These dual influences—Caribbean island remedies and Alabama Southern folk medicine—inform every product 
                    we craft.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Users className="w-8 h-8 text-terracotta-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-semibold text-sage-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    John's Heritage: African American Resilience
                  </h3>
                  <p className="text-sage-700 leading-relaxed">
                    John's African American heritage brings profound wisdom rooted in resilience and survival. His ancestors 
                    carried with them knowledge of plants and healing from across the Atlantic—knowledge that sustained 
                    communities through the most challenging times. From the fields to the folk healers, African American 
                    traditions have preserved ancient plant wisdom, understanding which roots heal, which leaves soothe, 
                    and which flowers bring wellness to body and spirit.
                  </p>
                  <p className="text-sage-700 leading-relaxed mt-4">
                    This legacy of natural medicine—born from necessity and refined through generations—lives on in John's 
                    commitment to creating products that honor these ancestral teachings while making them accessible to 
                    all who seek natural wellness.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-terracotta-50 p-6 rounded-xl my-8">
              <div className="flex items-start gap-4">
                <Leaf className="w-8 h-8 text-terracotta-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-semibold text-sage-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Why Natural Products Matter to Us
                  </h3>
                  <p className="text-sage-700 leading-relaxed">
                    For Tonja and John, natural products aren't just a trend—they're a return to ancestral wisdom that 
                    served communities long before modern pharmaceuticals. Both of their heritages share a deep understanding 
                    that nature provides everything we need for healing and wellness.
                  </p>
                  <p className="text-sage-700 leading-relaxed mt-4">
                    In Trinidad, natural remedies were the foundation of healthcare. In African American communities, 
                    herbal knowledge was life-sustaining medicine. In the American South, kitchen gardens and wild-crafted 
                    herbs kept families healthy for generations. These aren't nostalgic memories—they're living traditions 
                    that work.
                  </p>
                  <p className="text-sage-700 leading-relaxed mt-4">
                    Today's synthetic products often disconnect us from nature and our bodies. We believe in products that 
                    honor what our ancestors knew: that real healing comes from the earth, crafted with intention, respect, 
                    and love. Every tea we blend, every oil we infuse, every bath product we create carries this ancestral 
                    wisdom forward.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed text-center font-semibold text-sage-900 mt-8">
              When you choose JFRAI Family Flavorful Brews & Tings, you're not just buying products—you're connecting 
              with generations of wisdom, supporting family-owned wellness, and honoring the natural traditions that 
              have sustained communities across continents and centuries.
            </p>

            <div className="text-center mt-8 pt-8 border-t border-sage-200">
              <p className="text-2xl font-semibold text-sage-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                From Our Family to Yours
              </p>
              <p className="text-xl text-terracotta-600 mt-2">
                With Love, Tonja & John
              </p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="backdrop-blur-sm rounded-xl p-6 shadow-lg text-center" style={{ background: 'rgba(255, 218, 185, 0.95)' }}>
            <Heart className="w-10 h-10 text-terracotta-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-sage-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Family First
            </h3>
            <p className="text-sage-700 text-sm">
              Our family recipes, passed down through generations, are the heart of everything we create.
            </p>
          </div>

          <div className="backdrop-blur-sm rounded-xl p-6 shadow-lg text-center" style={{ background: 'rgba(255, 218, 185, 0.95)' }}>
            <Leaf className="w-10 h-10 text-sage-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-sage-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              100% Natural
            </h3>
            <p className="text-sage-700 text-sm">
              Only pure, natural ingredients—no synthetics, no compromises, just nature's best.
            </p>
          </div>

          <div className="backdrop-blur-sm rounded-xl p-6 shadow-lg text-center" style={{ background: 'rgba(255, 218, 185, 0.95)' }}>
            <Globe className="w-10 h-10 text-terracotta-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-sage-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ancestral Wisdom
            </h3>
            <p className="text-sage-700 text-sm">
              Honoring Caribbean, Southern, and African American healing traditions in every product.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
